/**
 * Configurable forms.
 *
 * The snapshot that seeds FormConfiguration. A super admin can relabel a field,
 * add help text, add or remove an option, reorder, and enable or disable an
 * optional field. What they cannot do is remove a locked field, change its
 * type, or make it optional: name, email, enquiry type, message and consent are
 * what the server validates and what a reply depends on, and a form builder
 * that can delete the consent checkbox is a form builder that can quietly stop
 * asking for consent.
 *
 * There is no field type that executes anything. The list is closed, the API
 * rejects anything outside it, and the rendered form is built by a component
 * from these descriptions rather than from anything an editor can type.
 */

export type SeedFormField = {
  name: string;
  label: string;
  type: "text" | "email" | "tel" | "textarea" | "select" | "checkbox" | "radio" | "number" | "date" | "url";
  help?: string;
  placeholder?: string;
  required?: boolean;
  enabled?: boolean;
  options?: { value: string; label: string }[];
  maxLength?: number;
  order: number;
  locked?: boolean;
};

export type SeedForm = {
  key: string;
  name: string;
  description: string;
  submitLabel: string;
  successNote: string;
  defaultInterest: string;
  fields: SeedFormField[];
};

/**
 * Enquiry types.
 *
 * "Volunteering" is the value the Volunteer With Us button preselects, which is
 * how that page's primary action keeps the visitor on the HISCUS site instead
 * of sending them into an application process that does not exist yet.
 */
export const interestOptions = [
  { value: "support", label: "Support our work (funding, partnership, CSR)" },
  { value: "volunteer", label: "Volunteering" },
  { value: "charity-trek", label: "Charity Trek" },
  { value: "local-collaboration", label: "Local collaboration (school, ward, health post, CBO)" },
  { value: "documentation", label: "Documentation and due diligence" },
  { value: "media-research", label: "Media or research" },
  { value: "general", label: "General enquiry" },
];

const shared: SeedFormField[] = [
  { name: "name", label: "Full name", type: "text", required: true, order: 0, locked: true },
  {
    name: "email",
    label: "Email address",
    type: "email",
    required: true,
    help: "So that somebody can reply to you.",
    order: 1,
    locked: true,
  },
  {
    name: "phone",
    label: "Phone or WhatsApp",
    type: "tel",
    help: "Optional. Include your country code.",
    order: 2,
  },
  { name: "country", label: "Country", type: "text", help: "Optional.", order: 3 },
  {
    name: "organisation",
    label: "Organisation",
    type: "text",
    help: "Optional. Foundation, company, school, ward or agency.",
    order: 4,
  },
  {
    name: "interest",
    label: "What is your enquiry about?",
    type: "select",
    required: true,
    options: interestOptions,
    order: 5,
    locked: true,
  },
  {
    name: "message",
    label: "Your message",
    type: "textarea",
    required: true,
    help: "Tell us what you have in mind, and any timeline you are working to.",
    maxLength: 4000,
    order: 6,
    locked: true,
  },
  {
    name: "consent",
    label: "I am happy for HISCUS to store these details and reply to my enquiry.",
    type: "checkbox",
    required: true,
    order: 7,
    locked: true,
  },
];

export const seedForms: SeedForm[] = [
  {
    key: "enquiry",
    name: "General enquiry",
    description: "One form for every kind of enquiry. Choose what it is about and it reaches the right person.",
    submitLabel: "Send enquiry",
    successNote: "Someone from HISCUS will reply to the email address you gave.",
    defaultInterest: "general",
    fields: shared,
  },
  {
    /**
     * The volunteer form.
     *
     * Today it is the enquiry form with "Volunteering" preselected, and that is
     * the whole of it: there is no application process, no eligibility rule, no
     * duration and no price, because HISCUS has not published any of those and
     * inventing them would commit the organisation to terms it never set.
     *
     * The detailed fields are coming from the client. When they arrive a super
     * admin adds them here in the panel, and the server keeps validating the
     * locked five regardless of what is added around them.
     */
    key: "volunteer",
    name: "Volunteering enquiry",
    description:
      "Tell us your dates, what you would like to contribute and anything you have already discussed with the team.",
    submitLabel: "Send volunteering enquiry",
    successNote: "Someone from HISCUS will reply to the email address you gave.",
    defaultInterest: "volunteer",
    fields: [
      ...shared,
      {
        name: "availableFrom",
        label: "Available from",
        type: "date",
        help: "Optional. Roughly when you could start.",
        order: 8,
      },
      {
        name: "availableUntil",
        label: "Available until",
        type: "date",
        help: "Optional.",
        order: 9,
      },
      {
        name: "skills",
        label: "Skills or interests",
        type: "textarea",
        help: "Optional. Teaching, stitching, documentation, construction, health, anything else.",
        maxLength: 1000,
        order: 10,
      },
    ],
  },
];
